'use client';

import {
  type ComponentPropsWithoutRef,
  type ElementType,
  type ReactNode,
} from 'react';
import {
  Tooltip as UiTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~/components/ui/tooltip';
import { useIsTruncated } from '~/hooks/useIsTruncated';
import { cn } from '~/lib/utils';

const LINE_CLAMP: Record<2 | 3 | 4, string> = {
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
};

type TruncatedTextProps<T extends ElementType = 'span'> = {
  as?: T;
  children: ReactNode;
  /** Full text shown on hover. Defaults to `children` when that is a string. */
  tooltip?: ReactNode;
  /** 1 = single-line ellipsis; 2–4 = line-clamp. Ignored if `className` already has `line-clamp-*`. */
  lines?: 1 | 2 | 3 | 4;
  /** Skip the overflow tooltip (e.g. when a parent already shows one). */
  disableTooltip?: boolean;
  className?: string;
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'children' | 'title' | 'className'>;

export default function TruncatedText<T extends ElementType = 'span'>({
  as,
  children,
  className,
  tooltip,
  lines = 1,
  disableTooltip = false,
  ...props
}: TruncatedTextProps<T>) {
  const Comp = as ?? 'span';
  const { ref, isTruncated } = useIsTruncated(children);
  const tooltipContent =
    tooltip ??
    (typeof children === 'string' || typeof children === 'number' ? String(children) : null);
  const hasLineClamp = /\bline-clamp-/.test(className ?? '');
  const showTooltip = !disableTooltip && isTruncated && !!tooltipContent && tooltipContent !== '—';

  const text = (
    <Comp
      ref={ref}
      className={cn(
        'min-w-0 max-w-full',
        !hasLineClamp && lines === 1 && 'truncate',
        !hasLineClamp && lines > 1 && LINE_CLAMP[lines as 2 | 3 | 4],
        className,
      )}
      {...props}
    >
      {children}
    </Comp>
  );

  if (!showTooltip) return text;

  return (
    <TooltipProvider delayDuration={400}>
      <UiTooltip>
        <TooltipTrigger asChild>{text}</TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-[400px] whitespace-pre-wrap break-words text-left font-normal"
        >
          {tooltipContent}
        </TooltipContent>
      </UiTooltip>
    </TooltipProvider>
  );
}
